/**
 * FAFSA Code Lookup
 *
 * Provides fuzzy matching of college names to their Federal School Codes.
 * Data extracted from the 2026-2027 Federal School Code List (public domain,
 * U.S. Department of Education).
 */
import fafsaCodes from './fafsaCodes.json';

// Build a normalized lookup on first access
let _normalizedEntries = null;

function getNormalizedEntries() {
  if (_normalizedEntries) return _normalizedEntries;

  _normalizedEntries = [];
  const seen = new Set();

  for (const [name, code] of Object.entries(fafsaCodes)) {
    const norm = normalize(name);
    if (seen.has(norm)) continue;
    seen.add(norm);
    _normalizedEntries.push({ norm, name, code });
  }

  return _normalizedEntries;
}

/**
 * Normalize a school name for comparison:
 * lowercase, strip punctuation, collapse whitespace.
 */
function normalize(name) {
  return (name || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Look up a FAFSA code by school name.
 * Uses fuzzy matching: exact → starts-with → word overlap scoring.
 *
 * @param {string} schoolName – the name from the college API (e.g. "Harvard University")
 * @returns {{ code: string, matchedName: string } | null}
 */
export function lookupFafsaCode(schoolName) {
  if (!schoolName) return null;

  const query = normalize(schoolName);
  if (!query) return null;

  const entries = getNormalizedEntries();

  // 1. Exact match
  for (const entry of entries) {
    if (entry.norm === query) {
      return { code: entry.code, matchedName: entry.name };
    }
  }

  // 2. FAFSA name starts with the query (PDF names often have address appended)
  for (const entry of entries) {
    if (entry.norm.startsWith(query + ' ') || entry.norm.startsWith(query)) {
      // Verify it's not a completely different school with a shared prefix
      if (entry.norm.length - query.length < 60) {
        return { code: entry.code, matchedName: entry.name };
      }
    }
  }

  // 3. Query starts with the FAFSA name (API name might be longer)
  for (const entry of entries) {
    if (query.startsWith(entry.norm + ' ') || query.startsWith(entry.norm)) {
      return { code: entry.code, matchedName: entry.name };
    }
  }

  // 4. Word overlap scoring
  const queryWords = new Set(query.split(' ').filter((w) => w.length > 1));
  let bestMatch = null;
  let bestScore = 0;

  for (const entry of entries) {
    const entryWords = new Set(entry.norm.split(' ').filter((w) => w.length > 1));
    let overlap = 0;
    for (const word of queryWords) {
      if (entryWords.has(word)) overlap++;
    }

    // Score = overlap / max(sizes) to favor specificity
    const score = overlap / Math.max(queryWords.size, entryWords.size);

    // Require at least 60% word overlap and at least 2 matching words
    if (score > bestScore && score >= 0.6 && overlap >= 2) {
      bestScore = score;
      bestMatch = entry;
    }
  }

  return bestMatch ? { code: bestMatch.code, matchedName: bestMatch.name } : null;
}
